<script>
import { mapActions } from 'vuex';
import DeleteWarning from './delete-warning.vue';
import i18n from "@/plugins/i18n";

export default {
    components: {
        DeleteWarning,
    },

    props: {
        selectedCompanyId: {
            type: Number,
            required: true
        },

        userId: {
            type: Number,
            required: true
        },

        years: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            items: null,
            fallbackValue: null,

            itemToAdd: {
                milestone_id: null,
                milestone_text: '',
                milestone_year: null,
            },
            addingItem: false,

            deletingItem: null,
        }
    },

    computed: {
        headers() {
            return [
                { key: 'delete', title: '', flex: 1 },
                { key: 'date', title: i18n.t('companyProfile.date'), flex: 3 },
                { key: 'name', title: i18n.t('companyProfile.name'), flex: 8 },
            ]
        },


    },

    mounted() {
        this.getItems();
    },

    methods: {
        ...mapActions('company', [
            'getMilestones',
            'createMilestones',
            'updateMilestones',
            'deleteMilestones',
        ]),

        getItems() {
            const params = {
                company_id: this.selectedCompanyId,
                is_deleted: false
            }

            this.getMilestones(params)
                .then(res => {
                    this.items = res.data;
                    if (res.data.length === 0) {
                        this.setAddingItem(true);
                    }
                })
        },

        createItem() {
            if (this.itemToAdd.milestone_year == null || this.itemToAdd.milestone_text == '') {
                return;
            }

            const params = {
                company_id: this.selectedCompanyId,
                created_by: this.userId,
                milestone_year: this.itemToAdd.milestone_year,
                milestone_text: this.itemToAdd.milestone_text
            }

            this.createMilestones(params)
                .then(res => {
                    this.itemToAdd.milestone_id = res.data.milestone_id;
                    this.items.push(this.itemToAdd);

                    this.sortItems();

                    this.handleResetAddingItem();

                    this.setAddingItem(false);

                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })

        },

        updateItem(e, key, id) {
            if (!e.target.value || e.target.value.length === 0) return;

            const params = {
                milestone_id: id,
                body: {
                    [`${key}`]: e.target.value
                }
            }

            this.updateMilestones(params)
                .then(res => {
                    this.setFallbackValue(null);
                    this.sortItems();
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.items.find(x => x.milestone_id == id)[`${key}`] == this.fallbackValue;
                    this.setFallbackValue(null);
                    this.$notify('error', response.data.messageName);
                })
        },

        deleteItem() {
            this.deleteMilestones({ milestone_id: this.deletingItem.milestone_id })
                .then(() => {
                    this.items = this.items.filter(x => x.milestone_id != this.deletingItem.milestone_id);
                    this.setDeletingItem(null);
                    this.$notify('success', '', true);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        /* handlers */

        handleResetAddingItem() {
            this.itemToAdd = {
                milestone_id: null,
                milestone_text: '',
                milestone_year: null,
            };

            this.setAddingItem(false);
        },

        sortItems() {
            this.items.sort((a, b) => b.milestone_year - a.milestone_year);
        },

        /* setters */

        setFallbackValue(val) {
            this.fallbackValue = val;
        },

        setAddingItem(bool) {
            this.addingItem = bool;
        },

        setDeletingItem(item) {
            this.deletingItem = item;
        },

    }
}
</script>

<template>
    <div class="items">
        <div class="item-headers">
            <div v-for="header in headers" :key="header.key" :style="{ flex: header.flex }">
                {{ header.title }}
            </div>
        </div>

        <div class="item" v-for="(item, i) in items" :key="i">
            <div class="delete-item-button">
                <button @click="setDeletingItem(item)">
                    <i class="fa-regular fa-trash-can"></i>
                </button>

                <delete-warning v-if="deletingItem == item" @onClose="setDeletingItem(null)" @onOk="deleteItem()" />
            </div>

            <select v-model="item.milestone_year" :style="{ flex: headers[1].flex }"
                @change="updateItem($event, 'milestone_year', item.milestone_id)">
                <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                </option>
            </select>

            <input type="text" :style="{ flex: headers[2].flex }" v-model="item.milestone_text" maxlength="255"
                @change="updateItem($event, 'milestone_text', item.milestone_id)"
                @focus="setFallbackValue(item.milestone_text)" @blur="setFallbackValue(null)" />
        </div>

        <div class="item" v-if="addingItem">
            <div class="delete-item-button">
                <button @click="handleResetAddingItem()" @keyup.enter="handleResetAddingItem()">
                    <i class="fa-regular fa-square-minus"></i>
                </button>
            </div>

            <select v-model="itemToAdd.milestone_year" :style="{ flex: headers[1].flex }" @change="createItem()">
                <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                </option>
            </select>

            <input type="text" :style="{ flex: headers[2].flex }" v-model="itemToAdd.milestone_text" maxlength="255"
                @change="createItem()" @focus="setFallbackValue(itemToAdd.milestone_text)"
                @blur="setFallbackValue(null)" />
        </div>

        <div class="add-item-ctr" v-if="items.length === 0 || !addingItem">
            <button @click="setAddingItem(true)" @keyup.enter="setAddingItem(true)" :disabled="addingItem">
                +
            </button>
        </div>
    </div>
</template>

<style lang='scss' scoped>
.items {
    min-height: 100px !important;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
}

.item-headers {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    >div {
        text-align: left;
        margin: .5em;
        color: var(--sustainfinity-navy);
        font-weight: bold;
        font-size: 1.1em;
    }
}

.item {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    align-content: flex-start;
    flex-wrap: wrap;

    >.delete-item-button {
        margin: .5em;
        flex: 1;
        display: flex;
        align-items: center;

        >.cannot-edit {
            font-size: .8em;
            font-style: italic;
            color: var(--invalid-input);
        }

        >button {
            display: flex;
            align-items: center;

            outline: 0;
            border: 0;

            background-color: transparent !important;


            &:focus {
                outline: thin solid lightgray !important;
            }

            >i {
                color: var(--invalid-input);
            }

            &:hover {
                >i {
                    color: var(--invalid-input--hover);
                }
            }

            &:disabled {
                pointer-events: none !important;

                >i {
                    color: gray;
                }
            }
        }

        position: relative;

    }

    >input,
    select {
        text-align: left;
        margin: .5em;
        outline: 0;
        border: 0;
        border-bottom: thin solid lightgray;
        background-color: whitesmoke;

        transition: border-bottom 500ms;

        &:focus {
            border-bottom: thin solid black;
        }

        &:disabled {
            border-radius: .2em;
            background-color: rgb(221, 221, 221);
            cursor: not-allowed;
        }
    }
}

.add-item-ctr {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 1em 0;

    >button {
        width: 50px;
        height: 30px;
        font-size: 1.2em;
        font-weight: bold;
        color: white;
        background-color: var(--sustainfinity-yellow);
        border-radius: .25em;

        outline: 0;
        border: 0;

        &:hover {
            background-color: var(--sustainfinity-yellow--hover);
        }

        &:focus {
            outline: thin solid gray !important;
        }
    }
}
</style>